import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { createConnectedWalletController } from './connected-wallet-controller.js';
export function useConnectedWalletCapability(client, surface) {
  const $ = _c(10);
  let t0;
  if ($[0] !== client.cacheKey || $[1] !== surface) {
    t0 = ["onramp", client.cacheKey, "connected-wallet-capability", surface];
    $[0] = client.cacheKey;
    $[1] = surface;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== client.connectedWallet || $[4] !== surface) {
    t1 = t2 => {
      const {
        signal
      } = t2;
      return client.connectedWallet?.capability(surface, signal) ?? Promise.resolve(false);
    };
    $[3] = client.connectedWallet;
    $[4] = surface;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      persist: false
    };
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t0 || $[8] !== t1) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      retry: false,
      staleTime: 60000,
      gcTime: 0,
      meta: t2
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useQuery(t3);
}
export function useConnectedWallet(client, options) {
  const $ = _c(30);
  const {
    surface,
    locale,
    active,
    operationId,
    createId,
    onComplete
  } = options;
  const t0 = operationId ?? null;
  let t1;
  if ($[0] !== client || $[1] !== createId || $[2] !== locale || $[3] !== surface || $[4] !== t0) {
    t1 = createConnectedWalletController(client, surface, locale, createId, t0);
    $[0] = client;
    $[1] = createId;
    $[2] = locale;
    $[3] = surface;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const actions = t1;
  const state = useSyncExternalStore(actions.subscribe, actions.getSnapshot, actions.getSnapshot);
  let t2;
  if ($[6] !== actions.dispose || $[7] !== client) {
    t2 = () => client.onDispose(actions.dispose);
    $[6] = actions.dispose;
    $[7] = client;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== actions || $[10] !== client) {
    t3 = [client, actions];
    $[9] = actions;
    $[10] = client;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[12] !== actions || $[13] !== onComplete) {
    t4 = () => {
      if (!onComplete) {
        return;
      }
      return actions.onComplete(onComplete);
    };
    t5 = [actions, onComplete];
    $[12] = actions;
    $[13] = onComplete;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[16] !== actions || $[17] !== active || $[18] !== operationId || $[19] !== state.operation) {
    t6 = () => {
      if (active && operationId && !state.operation) {
        actions.resume(operationId);
      }
    };
    t7 = [actions, active, operationId, state.operation];
    $[16] = actions;
    $[17] = active;
    $[18] = operationId;
    $[19] = state.operation;
    $[20] = t6;
    $[21] = t7;
  } else {
    t6 = $[20];
    t7 = $[21];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[22] !== actions || $[23] !== active || $[24] !== state.operation) {
    t8 = () => {
      const operation = state.operation;
      if (!active || !operation || ["complete", "route_failed", "refund"].includes(operation.state)) {
        return;
      }
      const timer = setInterval(() => void actions.refresh(), 7000);
      return () => clearInterval(timer);
    };
    t9 = [actions, active, state.operation];
    $[22] = actions;
    $[23] = active;
    $[24] = state.operation;
    $[25] = t8;
    $[26] = t9;
  } else {
    t8 = $[25];
    t9 = $[26];
  }
  useEffect(t8, t9);
  let t10;
  if ($[27] !== actions || $[28] !== state) {
    t10 = {
      state,
      actions
    };
    $[27] = actions;
    $[28] = state;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}